import { Outlet } from 'react-router';
import type { RouteObject } from 'react-router';

import {
  Liveboard,
  RequireThoughtspotUserBucket,
  UnauthorizedAccess,
} from '@jane/business-admin/components';
import { useHasPermissions } from '@jane/business-admin/hooks';
import { ThoughtspotBucketNames } from '@jane/business-admin/types';
import { Permission } from '@jane/shared/auth';
import { load } from '@jane/shared/util';

const Analytics = load(() => import('../features/Analytics'));

const GatedAnalyticsRoute = () => {
  const isAllowed = useHasPermissions([Permission.ViewAnalytics]);

  return !isAllowed ? (
    <UnauthorizedAccess resourceName="analytics" variant="view" />
  ) : (
    <Analytics />
  );
};

export const analyticsRoutes = (): RouteObject[] => [
  {
    path: '/analytics',
    element: <GatedAnalyticsRoute />,
    children: [
      {
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.DEFAULT_LIVEBOARDS}
          >
            <Outlet />
          </RequireThoughtspotUserBucket>
        ),
        children: [
          {
            path: 'customers',
            element: <Liveboard liveboardRoute="customers" />,
          },
          {
            path: 'fulfillment',
            element: <Liveboard liveboardRoute="fulfillment" />,
          },
          {
            path: 'inventory-forecasts',
            element: <Liveboard liveboardRoute="inventory-forecasts" />,
          },
          {
            path: 'jane-gold',
            element: <Liveboard liveboardRoute="jane-gold" />,
          },
          {
            path: 'kpis',
            element: <Liveboard liveboardRoute="kpis" />,
          },
          {
            path: 'on-menu-merchandising',
            element: <Liveboard liveboardRoute="on-menu-merchandising" />,
          },
          {
            path: 'marketing-attribution',
            element: <Liveboard liveboardRoute="marketing-attribution" />,
          },
          {
            path: 'product-reviews',
            element: <Liveboard liveboardRoute="product-reviews" />,
          },
          {
            path: 'sales',
            element: <Liveboard liveboardRoute="sales" />,
          },
          {
            path: 'store-reviews',
            element: <Liveboard liveboardRoute="store-reviews" />,
          },
          {
            path: 'web-traffic',
            element: <Liveboard liveboardRoute="web-traffic" />,
          },
        ],
      },
      {
        path: 'google-traffic',
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.DEFAULT_LIVEBOARDS}
            unauthorizedResourceName="the Google Traffic dashboard"
          >
            <Liveboard liveboardRoute="google-traffic" />
          </RequireThoughtspotUserBucket>
        ),
      },
      {
        path: 'marketplace',
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.DEFAULT_LIVEBOARDS}
            unauthorizedResourceName="the marketplace dashboard"
          >
            <Liveboard liveboardRoute="marketplace" />
          </RequireThoughtspotUserBucket>
        ),
      },
      {
        path: 'off-menu-advertising',
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.OFF_MENU}
            upsellScreenTitle="Off-Menu Advertising"
            upsellScreenDescription="Attract shoppers from the open web and gain insights into your reach with the most accurate and verified sales data within the industry."
          >
            <Liveboard liveboardRoute="off-menu-advertising" />
          </RequireThoughtspotUserBucket>
        ),
      },
      {
        path: 'off-menu-merchandising',
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.OFF_MENU_CHURNED}
          >
            <Liveboard liveboardRoute="off-menu-merchandising" />
          </RequireThoughtspotUserBucket>
        ),
      },
      {
        path: 'pricing',
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.DEFAULT_LIVEBOARDS}
            unauthorizedResourceName="the pricing dashboard"
          >
            <Liveboard liveboardRoute="pricing" />
          </RequireThoughtspotUserBucket>
        ),
      },
      {
        path: 'printers',
        element: (
          <RequireThoughtspotUserBucket
            internalOnly
            bucketName={ThoughtspotBucketNames.DEFAULT_LIVEBOARDS}
            unauthorizedResourceName="the printers dashboard"
          >
            <Liveboard liveboardRoute="printers" />
          </RequireThoughtspotUserBucket>
        ),
      },
      {
        path: 'sdk',
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.INTEGRATOR_DASHBOARDS}
            unauthorizedResourceName="the SDK dashboard"
          >
            <Liveboard liveboardRoute="sdk" />
          </RequireThoughtspotUserBucket>
        ),
      },
      {
        path: 'trends',
        element: (
          <RequireThoughtspotUserBucket
            bucketName={ThoughtspotBucketNames.DEFAULT_LIVEBOARDS}
            unauthorizedResourceName="the trends dashboard"
          >
            <Liveboard liveboardRoute="trends" />
          </RequireThoughtspotUserBucket>
        ),
      },
    ],
  },
];
